/**
 * SecureEmbed Dashboard Landing Page
 *
 * Environment tiles with live health metrics and quick access
 * Shows REAL infrastructure status from live API calls
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Server,
  AlertTriangle,
  RefreshCw,
  Database,
  CloudOff,
  ArrowUpDown,
  Globe,
} from 'lucide-react';
import { metricsApi, operationsApi, demoEnvApi, ENV_COLORS, HealthResponse } from '../services/api';
import { PROVISIONED_ENVIRONMENTS } from '../contexts/EnvironmentContext';
import AuditTrailCard from '../components/AuditTrailCard';

// Environment configuration
const ENVIRONMENTS = ['dev', 'test', 'uat', 'prod', 'shared'] as const;
type Environment = typeof ENVIRONMENTS[number];

// Health indicator component with optional reasons tooltip
const HealthIndicator: React.FC<{ status: string; reasons?: string[] }> = ({ status, reasons }) => {
  const label = status === 'healthy' ? 'Healthy' : status === 'warning' ? 'Warning' : status === 'critical' ? 'Critical' : status === 'not_deployed' ? 'Not Deployed' : 'Unknown';
  const tooltip = reasons?.length
    ? `${label}\n${reasons.map(r => `• ${r}`).join('\n')}`
    : label;

  const baseClass = 'w-3 h-3 rounded-full';
  switch (status) {
    case 'healthy':
      return <span className={`${baseClass} bg-green-500 animate-pulse`} title={tooltip} />;
    case 'warning':
      return <span className={`${baseClass} bg-yellow-500 animate-pulse`} title={tooltip} />;
    case 'critical':
      return <span className={`${baseClass} bg-red-500 animate-pulse`} title={tooltip} />;
    case 'not_deployed':
      return <span className={`${baseClass} bg-gray-300`} title={tooltip} />;
    default:
      return <span className={`${baseClass} bg-gray-400`} title={tooltip} />;
  }
};

// Environment tile component
interface EnvironmentTileProps {
  env: Environment;
  health: HealthResponse | null;
  isLoading: boolean;
  error: Error | null;
  deploymentStatus?: string;
}

const EnvironmentTile: React.FC<EnvironmentTileProps> = ({ env, health, isLoading, error, deploymentStatus }) => {
  const colors = ENV_COLORS[env];

  const isDeployed = deploymentStatus === 'deployed';
  const overallStatus = (() => {
    if (!deploymentStatus || deploymentStatus === 'unknown' || deploymentStatus === 'not_deployed') {
      return 'not_deployed';
    }
    if (deploymentStatus === 'deploying' || deploymentStatus === 'destroying') {
      return 'warning';
    }
    if (deploymentStatus === 'partial') {
      return 'warning';
    }
    return health?.overall_status || 'healthy';
  })();

  const components = health?.components;

  const fmt = (v: any, suffix = '') => v != null ? `${typeof v === 'number' ? (Number.isInteger(v) ? v : Number(v).toFixed(1)) : v}${suffix}` : '—';

  // Determine warning/unknown reasons by scanning components
  const getWarningReasons = (): string[] => {
    const reasons: string[] = [];
    if (!components) return reasons;

    Object.entries(components).forEach(([k, v]) => {
      if (v?.status === 'warning' || v?.status === 'critical') {
        reasons.push(`${k.replace(/_/g, ' ')}: ${v.status}`);
      }
    });

    const unknownComponents = Object.entries(components)
      .filter(([, v]) => v?.status === 'unknown' || v?.status === 'not_found' || v?.status === 'no_data')
      .map(([k]) => k.replace(/_/g, ' '));
    if (unknownComponents.length > 0) {
      reasons.push(`${unknownComponents.length} services: no metrics available`);
    }

    return reasons;
  };

  // Check if a component has active data (not unknown/not_found/no_data)
  const isActive = (c: any) => c && c.status !== 'unknown' && c.status !== 'not_found' && c.status !== 'no_data';

  // Colour for individual stat lines based on component status
  const getLineColor = (componentStatus?: string) => {
    if (componentStatus === 'critical') return 'text-red-600';
    if (componentStatus === 'warning') return 'text-yellow-600';
    return '';
  };

  // Component sections config
  interface MetricLine { label: string; value: string; color: string }
  interface ComponentSection {
    title: string;
    icon: React.ElementType;
    metrics: MetricLine[];
  }

  const buildSections = (): ComponentSection[] => {
    if (!components) return [];
    const sections: ComponentSection[] = [];

    // Infrastructure
    const apiGw = components.api_gateway;
    const lambda = components.lambda;
    const opensearch = components.opensearch;
    const infraMetrics: MetricLine[] = [];
    if (isActive(apiGw)) {
      infraMetrics.push(
        { label: 'API p99', value: fmt(apiGw?.latency_p99, 'ms'), color: getLineColor(apiGw?.status) },
        { label: 'API 5xx', value: fmt(apiGw?.['5xx_rate'], '%'), color: getLineColor(apiGw?.status) },
      );
    }
    if (isActive(lambda)) {
      infraMetrics.push(
        { label: 'Invocations', value: fmt(lambda?.invocations), color: getLineColor(lambda?.status) },
        { label: 'Error rate', value: fmt(lambda?.error_rate, '%'), color: getLineColor(lambda?.status) },
      );
    }
    if (isActive(opensearch)) {
      infraMetrics.push(
        { label: 'OS CPU', value: fmt(opensearch?.cpu, '%'), color: getLineColor(opensearch?.status) },
        { label: 'OS JVM', value: fmt(opensearch?.memory, '%'), color: getLineColor(opensearch?.status) },
        { label: 'Cluster', value: opensearch?.cluster_status || '—', color: getLineColor(opensearch?.status) },
      );
    }
    if (infraMetrics.length > 0) sections.push({ title: 'Infrastructure', icon: Server, metrics: infraMetrics });

    // Data
    const aurora = components.aurora;
    const dynamodb = components.dynamodb;
    const s3 = components.s3;
    const dataMetrics: MetricLine[] = [];
    if (isActive(aurora)) {
      dataMetrics.push(
        { label: 'DB CPU', value: fmt(aurora?.cpu, '%'), color: getLineColor(aurora?.status) },
        { label: 'Connections', value: fmt(aurora?.connections), color: getLineColor(aurora?.status) },
      );
    }
    if (isActive(dynamodb)) {
      dataMetrics.push(
        { label: 'Tables', value: fmt(dynamodb?.total_tables), color: '' },
        { label: 'Items', value: fmt(dynamodb?.total_items), color: '' },
      );
    }
    if (isActive(s3)) {
      dataMetrics.push(
        { label: 'Buckets', value: fmt(s3?.total_buckets), color: '' },
        { label: 'Objects', value: fmt(s3?.total_objects), color: '' },
      );
    }
    if (dataMetrics.length > 0) sections.push({ title: 'Data', icon: Database, metrics: dataMetrics });

    // Services
    const step = components.step_functions;
    const eks = components.eks;
    const cognito = components.cognito;
    const cf = components.cloudfront;
    const svcMetrics: MetricLine[] = [];
    if (isActive(step)) {
      svcMetrics.push({ label: 'Workflows', value: fmt(step?.success_rate, '%'), color: getLineColor(step?.status) });
    }
    if (isActive(eks)) {
      svcMetrics.push(
        { label: 'Nodes', value: fmt(eks?.node_count), color: '' },
        { label: 'EKS CPU', value: fmt(eks?.cpu_utilization, '%'), color: getLineColor(eks?.status) },
      );
    }
    if (isActive(cognito)) {
      svcMetrics.push({ label: 'Auth', value: cognito?.deployed ? 'Active' : 'Inactive', color: '' });
    }
    if (isActive(cf)) {
      svcMetrics.push(
        { label: 'CDNs', value: fmt(cf?.total_distributions), color: '' },
        { label: 'CDN 5xx', value: fmt(cf?.error_rate_5xx, '%'), color: getLineColor(cf?.status) },
      );
    }
    if (svcMetrics.length > 0) sections.push({ title: 'Services', icon: Globe, metrics: svcMetrics });

    return sections;
  };

  if (isLoading) {
    return (
      <div
        className="rounded-xl border-2 p-5 animate-pulse"
        style={{ borderColor: colors.primary, backgroundColor: colors.bg }}
      >
        <div className="flex items-center gap-6">
          <div className="h-6 w-16 bg-gray-300 rounded" />
          <div className="flex-1 flex gap-8">
            <div className="h-4 w-24 bg-gray-300 rounded" />
            <div className="h-4 w-20 bg-gray-300 rounded" />
          </div>
          <div className="w-3 h-3 rounded-full bg-gray-300" />
        </div>
      </div>
    );
  }

  if (error || !isDeployed) {
    return (
      <div
        className="rounded-xl border-2 p-5 relative overflow-hidden"
        style={{ borderColor: '#d1d5db', backgroundColor: '#f9fafb' }}
      >
        <div className="flex items-center gap-6">
          <h3 className="text-lg font-bold uppercase w-16" style={{ color: colors.primary }}>
            {env}
          </h3>
          <div className="flex-1 flex items-center gap-3 text-gray-500">
            <CloudOff className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium">Not Deployed</span>
            <span className="text-xs text-gray-400">Environment inactive</span>
          </div>
          <HealthIndicator status="not_deployed" />
        </div>
      </div>
    );
  }

  const warningReasons = getWarningReasons();
  const sections = buildSections();

  // Top indicator = highest severity colour
  const indicatorColor = overallStatus === 'critical' ? 'bg-red-500' : overallStatus === 'warning' ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <Link
      to={`/monitor?env=${env}`}
      className="block rounded-xl border-2 p-5 relative overflow-hidden hover:shadow-lg transition-all cursor-pointer"
      style={{ borderColor: colors.primary, backgroundColor: colors.bg }}
    >
      {/* Severity top bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${indicatorColor}`} />

      {/* Env name header */}
      <div className="flex items-center gap-3 mb-3">
        <HealthIndicator status={overallStatus} reasons={warningReasons} />
        <h3 className="text-lg font-bold uppercase" style={{ color: colors.primary }}>
          {env}
        </h3>
      </div>

      {/* Component sections */}
      {sections.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {sections.map(section => {
            const SIcon = section.icon;
            return (
              <div key={section.title} className="bg-white/60 rounded-lg p-3 border border-gray-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <SIcon className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{section.title}</span>
                </div>
                <div className="space-y-1">
                  {section.metrics.map((m, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 text-xs">{m.label}</span>
                      <span className={`font-semibold text-xs ${m.color}`} style={{ color: m.color ? undefined : colors.text }}>
                        {m.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm text-gray-400">No active component data</div>
      )}

      {/* Warning reasons */}
      {warningReasons.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-200 flex flex-wrap gap-x-4 gap-y-1">
          {warningReasons.map((reason, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-amber-700">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span>{reason}</span>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
};

// Severity score for sorting
const SEVERITY_SCORE: Record<string, number> = {
  not_deployed: 0,
  healthy: 1,
  warning: 2,
  critical: 3,
};

// Environment rank for natural ordering
const ENV_RANK: Record<string, number> = { dev: 0, test: 1, uat: 2, prod: 3 };

// Main Dashboard component
const Dashboard: React.FC = () => {
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [apiError, setApiError] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<'env' | 'severity'>('env');

  // Fetch deployment status for all environments
  const statusQuery = useQuery({
    queryKey: ['status'],
    queryFn: async () => {
      try {
        const result = await operationsApi.getStatus();
        setApiError(null);
        return result;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch status';
        console.error('Status API error:', message);
        setApiError(message);
        return null;
      }
    },
    staleTime: 30000,
    refetchInterval: 30000,
  });

  // Fetch health for all environments (30s refresh). ENVIRONMENTS is a fixed
  // module-level constant (never changes length/order at runtime), but calling
  // useQuery inside .map() still violates react-hooks/rules-of-hooks under
  // static analysis — useQueries is TanStack Query's supported hook for
  // exactly this "list of parallel queries" shape, and returns the same
  // per-query result objects (.data/.isLoading/.error/.refetch) as useQuery.
  const envHealthQueries = useQueries({
    queries: ENVIRONMENTS.map(env => ({
      queryKey: ['health', env],
      queryFn: async () => {
        try {
          return await metricsApi.getHealth(env);
        } catch (error) {
          console.error(`Health API error for ${env}:`, error instanceof Error ? error.message : 'Unknown error');
          return null;
        }
      },
      staleTime: 30000,
      refetchInterval: 30000,
    })),
  });

  // R2: cached drift verdicts for the provisioned envs (dev, test) — drives the
  // slim drifted-baseline banner. Same ['drift', env] keys as the Environments
  // hub cards, so the cache is shared. Renders nothing while loading/unknown.
  const driftQueries = useQueries({
    queries: PROVISIONED_ENVIRONMENTS.map(env => ({
      queryKey: ['drift', env],
      queryFn: () => demoEnvApi.getDrift(env),
      refetchInterval: 60000,
      staleTime: 30000,
    })),
  });
  const driftedEnvs = PROVISIONED_ENVIRONMENTS
    .map((env, i) => ({ env, verdict: driftQueries[i].data?.verdict ?? null }))
    .filter(d => d.verdict?.drifted === true);

  // Aggregate health data
  const healthData: Record<Environment, HealthResponse | null> = ENVIRONMENTS.reduce((acc, env, index) => {
    acc[env] = envHealthQueries[index].data || null;
    return acc;
  }, {} as Record<Environment, HealthResponse | null>);

  // Derive deployment status from LIVE health checks
  const deploymentStatus: Record<Environment, string> = ENVIRONMENTS.reduce((acc, env, index) => {
    const apiStatus = statusQuery.data?.environments?.[env]?.status || 'unknown';
    const healthQuery = envHealthQueries[index];
    const health = healthQuery.data || null;

    if (apiStatus === 'deploying' || apiStatus === 'destroying') {
      acc[env] = apiStatus;
      return acc;
    }

    if (healthQuery.isLoading) {
      acc[env] = apiStatus;
      return acc;
    }

    if (health?.components) {
      const coreKeys: (keyof HealthResponse['components'])[] = ['api_gateway', 'lambda', 'opensearch', 'cognito'];
      const statuses = coreKeys
        .map(k => health.components[k]?.status)
        .filter(Boolean) as string[];

      if (statuses.length === 0) {
        acc[env] = 'not_deployed';
        return acc;
      }

      const liveStatuses = ['healthy', 'warning', 'critical', 'stopped', 'error'];
      acc[env] = statuses.some((s: string) => liveStatuses.includes(s)) ? 'deployed' : 'not_deployed';
    } else {
      acc[env] = 'not_deployed';
    }

    return acc;
  }, {} as Record<Environment, string>);

  const isAnyLoading = envHealthQueries.some(q => q.isLoading) || statusQuery.isLoading;

  // Manual refresh
  const handleRefresh = () => {
    statusQuery.refetch();
    envHealthQueries.forEach(q => q.refetch());
    setLastRefresh(new Date());
  };

  // Compute overall status per environment for sorting
  const getEnvSeverity = (env: Environment): number => {
    const status = deploymentStatus[env];
    if (!status || status === 'not_deployed' || status === 'unknown') return SEVERITY_SCORE.not_deployed;
    const health = healthData[env];
    return SEVERITY_SCORE[health?.overall_status || 'healthy'] ?? 1;
  };

  // Sort environments by env rank or severity
  const sortedEnvironments = useMemo(() => {
    const envsCopy = [...ENVIRONMENTS];
    if (sortMode === 'env') {
      envsCopy.sort((a, b) => (ENV_RANK[a] ?? 0) - (ENV_RANK[b] ?? 0));
    } else {
      envsCopy.sort((a, b) => getEnvSeverity(b) - getEnvSeverity(a));
    }
    return envsCopy;
  }, [sortMode, deploymentStatus, healthData]);

  return (
    <div className="space-y-6">
      {/* Drifted-baseline banner — shown ONLY when a provisioned env's cached
          drift verdict is drifted === true (never for unknown/loading). */}
      {driftedEnvs.length > 0 && (
        <div className="space-y-2">
          {driftedEnvs.map(({ env, verdict }) => (
            <div
              key={env}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900"
            >
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span>
                <b>{env.toUpperCase()}</b> has drifted from its pinned baseline (
                {verdict?.changes ?? 0} change{(verdict?.changes ?? 0) === 1 ? '' : 's'})
              </span>
              <Link
                to="/environments"
                className="ml-auto font-semibold text-iflow-700 hover:text-iflow-900 whitespace-nowrap"
              >
                Environments →
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Environment Overview</h2>
          <p className="text-gray-500 mt-1">Monitor all SecureEmbed environments at a glance</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSortMode(prev => prev === 'env' ? 'severity' : 'env')}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
            title={`Sort: ${sortMode === 'env' ? 'Environment order' : 'Severity order'}`}
          >
            <ArrowUpDown className="w-4 h-4" />
            {sortMode === 'env' ? 'Env Order' : 'Severity'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={isAnyLoading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isAnyLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* API Error Banner */}
      {apiError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">API Connection Error</p>
              <p className="text-sm text-red-700">{apiError}</p>
            </div>
            <button
              onClick={handleRefresh}
              className="text-sm text-red-700 hover:text-red-900 underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Environment cards — horizontal layout, sorted by severity */}
      <div className="space-y-4">
        {sortedEnvironments.map((env) => {
          const index = ENVIRONMENTS.indexOf(env);
          return (
            <EnvironmentTile
              key={env}
              env={env}
              health={healthData[env]}
              isLoading={envHealthQueries[index].isLoading}
              error={envHealthQueries[index].error as Error | null}
              deploymentStatus={deploymentStatus[env]}
            />
          );
        })}
      </div>

      {/* Audit Trail Health */}
      <AuditTrailCard />

      {/* Last refresh timestamp */}
      <div className="text-center text-xs text-gray-400">
        Last refreshed: {lastRefresh.toLocaleTimeString()}
      </div>
    </div>
  );
};

export default Dashboard;
